/**
 * Makes a photo smaller in the browser before it is uploaded to Sharetribe.
 *
 * Sharetribe bills an upload by its size: 30 credits plus ~225 per MB. Phone photos were
 * ~605 KB on average (often several MB), while the largest size the site ever shows is
 * 2400 px wide. A 2000 px JPEG at 80% quality is usually 200–400 KB and looks the same on
 * screen; only zooming in full screen on a very large display can show the difference.
 *
 * The original file is uploaded as it is when:
 * - the browser cannot read it (HEIC on most browsers, GIF, anything that is not a photo),
 * - it is already small enough (both under `maxSide` and under SMALL_FILE_BYTES),
 * - anything goes wrong while resizing,
 * - or the result would not be smaller.
 *
 * Photos keep their orientation: the browser applies the EXIF rotation when it reads the
 * file. Transparent parts of a PNG become white (JPEG has no transparency).
 */

const RESIZABLE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const SMALL_FILE_BYTES = 300 * 1024;

const readImage = file => {
  if (typeof window.createImageBitmap === 'function') {
    return window.createImageBitmap(file, { imageOrientation: 'from-image' });
  }
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = e => {
      URL.revokeObjectURL(url);
      reject(e);
    };
    img.src = url;
  });
};

const toJpegName = name => (name || 'photo').replace(/\.[^./]+$/, '') + '.jpg';

/**
 * @param {File} file the picked photo
 * @param {Object} [options]
 * @param {number} [options.maxSide] longest side in pixels after resizing
 * @param {number} [options.quality] JPEG quality, 0–1
 * @returns {Promise<File>} a smaller JPEG, or the original file
 */
export const resizeImageFile = (file, { maxSide = 2000, quality = 0.8 } = {}) => {
  const canResize =
    typeof window !== 'undefined' &&
    typeof document !== 'undefined' &&
    file &&
    RESIZABLE_TYPES.includes(file.type);
  if (!canResize) {
    return Promise.resolve(file);
  }

  return readImage(file)
    .then(image => {
      const width = image.width;
      const height = image.height;
      const scale = Math.min(1, maxSide / Math.max(width, height));
      if (!width || !height || (scale === 1 && file.size <= SMALL_FILE_BYTES)) {
        return file;
      }

      const canvas = document.createElement('canvas');
      canvas.width = Math.round(width * scale);
      canvas.height = Math.round(height * scale);
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        return file;
      }
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
      if (typeof image.close === 'function') {
        image.close();
      }

      return new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', quality)).then(blob =>
        blob && blob.size < file.size
          ? new File([blob], toJpegName(file.name), {
              type: 'image/jpeg',
              lastModified: file.lastModified,
            })
          : file
      );
    })
    .catch(() => file);
};
